import type {
	CollisionDetection,
	DragEndEvent,
	DragOverEvent,
	DragStartEvent,
} from '@dnd-kit/core';
import {
	closestCenter,
	DndContext,
	DragOverlay,
	PointerSensor,
	pointerWithin,
	rectIntersection,
	useDroppable,
	useSensor,
	useSensors,
} from '@dnd-kit/core';
import {
	arrayMove,
	SortableContext,
	useSortable,
	verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { faFire, faGripVertical } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { SavedSearchModel } from '@supply-mover-2-0/core';
import clsx from 'clsx';
import {
	useCallback,
	useMemo,
	useRef,
	useState,
	type ReactElement,
} from 'react';
import { createPortal } from 'react-dom';
import { Button } from '../../../components/forms/Button';
import { useThemeColors } from '../../../providers/ThemeProvider';
import { useTranslation } from '../../../providers/TranslationProvider';
import { tailwindClasses } from '../../../themes/tailwind-classes';

interface SavedSearchManageTabsProps {
	/** All available saved searches */
	searches: Array<SavedSearchModel>;
	/** Current tab IDs in order */
	currentTabIds: Array<string>;
	/** Callback when tabs are saved */
	onSave: (tabIds: Array<string>) => void;
	/** Callback when cancelled */
	onCancel: () => void;
	/** Whether saving is in progress */
	isSaving?: boolean;
	/** Whether user has custom tabs */
	isCustom?: boolean;
	/** Whether user's custom tabs are currently enabled */
	isEnabled?: boolean;
	/** Callback to disable custom tabs (temporarily fall back to admin/default) */
	onDisable?: () => void;
	/** Callback to re-enable custom tabs */
	onEnable?: () => void;
	/** Callback to permanently reset tabs to defaults */
	onReset?: () => void;
	/**
	 * Heading over the tabs column. Defaults to "My Tabs"; the audience tab
	 * strip editor (PD-14278) reuses this component for a strip that belongs
	 * to everyone or a role, not the person editing it.
	 */
	tabsLabel?: string;
}

interface TabItem {
	id: string;
	name: string;
	type: SavedSearchModel['type'];
	totalUseCount: number;
}

/**
 * Get consistent ID for a saved search.
 * - Default searches use their list config ID (e.g., "new-leads-list")
 * - Saved searches use "saved:{id}" format (e.g., "saved:45")
 */
function getSearchId(search: SavedSearchModel): string {
	if (search.type === 'default') {
		return String(search.id);
	}
	return `saved:${search.id}`;
}

/**
 * Format usage count for display.
 */
const TABS_DROP_ZONE = 'tabs-drop-zone';
const AVAILABLE_DROP_ZONE = 'available-drop-zone';
const CONTAINER_IDS = new Set([TABS_DROP_ZONE, AVAILABLE_DROP_ZONE]);

function formatUseCount(count: number): string {
	if (count < 1000) {
		return String(count);
	}
	return `${Math.floor(count / 1000)}k+`;
}

/**
 * Draggable tab chip used in both My Tabs and Available Lists columns.
 * Entire container is draggable, grip icon is visual indicator only.
 */
function DraggableTabChip({ item }: { item: TabItem }): ReactElement {
	const colors = useThemeColors();
	const { t } = useTranslation();
	const {
		attributes,
		listeners,
		setNodeRef,
		transform,
		transition,
		isDragging,
	} = useSortable({ id: item.id });

	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
		opacity: isDragging ? 0.5 : 1,
	};

	return (
		<div
			ref={setNodeRef}
			style={style}
			{...attributes}
			{...listeners}
			className={clsx(
				'flex items-center gap-2 px-3 py-2 rounded-lg cursor-grab active:cursor-grabbing',
				'border min-w-0',
				colors.classes.bgSecondary,
				colors.classes.borderDefault,
			)}
		>
			<FontAwesomeIcon
				icon={faGripVertical}
				className="h-3.5 w-3.5 shrink-0 text-faint"
			/>
			<span
				className={clsx(
					'flex-1 min-w-0 truncate text-sm',
					colors.classes.textPrimary,
				)}
			>
				{t(item.name)}
			</span>
			<div className="w-12 shrink-0 flex justify-end">
				{item.totalUseCount > 0 && (
					<div
						className="flex items-center gap-1 px-1.5 py-0.5 text-xs bg-warning-solid text-white rounded"
						title={t('Popularity')}
					>
						<FontAwesomeIcon icon={faFire} className="h-2.5 w-2.5" />
						<span>{formatUseCount(item.totalUseCount)}</span>
					</div>
				)}
			</div>
			<div className="w-16 shrink-0 flex justify-end">
				<TypeBadge type={item.type} />
			</div>
		</div>
	);
}

function TabChipOverlay({ item }: { item: TabItem }): ReactElement {
	const colors = useThemeColors();
	const { t } = useTranslation();

	return (
		<div
			className={clsx(
				'flex items-center gap-2 px-3 py-2 rounded-lg',
				'border shadow-lg min-w-0',
				colors.classes.bgPrimary,
				colors.classes.borderDefault,
			)}
		>
			<FontAwesomeIcon
				icon={faGripVertical}
				className="h-3.5 w-3.5 shrink-0 text-faint"
			/>
			<span
				className={clsx(
					'flex-1 min-w-0 truncate text-sm',
					colors.classes.textPrimary,
				)}
			>
				{t(item.name)}
			</span>
			<div className="w-12 shrink-0 flex justify-end">
				{item.totalUseCount > 0 && (
					<div
						className="flex items-center gap-1 px-1.5 py-0.5 text-xs bg-warning-solid text-white rounded"
						title={t('Popularity')}
					>
						<FontAwesomeIcon icon={faFire} className="h-2.5 w-2.5" />
						<span>{formatUseCount(item.totalUseCount)}</span>
					</div>
				)}
			</div>
			<div className="w-16 shrink-0 flex justify-end">
				<TypeBadge type={item.type} />
			</div>
		</div>
	);
}

/**
 * Droppable zone wrapper — must be rendered as a CHILD of DndContext
 * so that useDroppable registers with the correct context.
 */
function DroppableZone({
	id,
	children,
}: {
	id: string;
	children: React.ReactNode;
}): ReactElement {
	const colors = useThemeColors();
	const { setNodeRef, isOver } = useDroppable({ id });

	return (
		<div
			ref={setNodeRef}
			className={clsx(
				'space-y-2 min-h-[200px] max-h-[480px] overflow-y-auto',
				'p-2 rounded-lg border-2 border-dashed',
				colors.classes.borderDefault,
				isOver && tailwindClasses.badgeInfo,
			)}
		>
			{children}
		</div>
	);
}

function TypeBadge({ type }: { type: SavedSearchModel['type'] }): ReactElement {
	const { t } = useTranslation();

	const styles = {
		default: 'bg-subtle text-muted',
		mine: tailwindClasses.badgeInfo,
		shared: tailwindClasses.badgeSuccess,
	};

	const labels = {
		default: 'Default',
		mine: 'Mine',
		shared: 'Shared',
	};

	return (
		<span
			className={clsx('px-2 py-0.5 text-xs font-medium rounded', styles[type])}
		>
			{t(labels[type])}
		</span>
	);
}

/**
 * Manage Tabs panel for reordering and selecting which tabs appear.
 * Uses drag-and-drop to add/remove/reorder tabs.
 */
export function SavedSearchManageTabs({
	searches,
	currentTabIds,
	onSave,
	onCancel,
	isSaving = false,
	isCustom = false,
	isEnabled = true,
	onDisable,
	onEnable,
	onReset,
	tabsLabel,
}: SavedSearchManageTabsProps): ReactElement {
	const colors = useThemeColors();
	const { t } = useTranslation();

	// Local state for pending changes
	const [tabIds, setTabIds] = useState<Array<string>>(currentTabIds);
	const [activeId, setActiveId] = useState<string | null>(null);

	// Store previous state for cancellation recovery
	const previousTabIds = useRef<Array<string>>(currentTabIds);

	// Build map of search ID to search details
	const searchMap = useMemo(() => {
		const map = new Map<string, SavedSearchModel>();
		for (const search of searches) {
			map.set(getSearchId(search), search);
		}
		return map;
	}, [searches]);

	// Build tab items from current tab IDs
	const tabItems: Array<TabItem> = useMemo(
		() =>
			tabIds
				.map((id) => {
					const search = searchMap.get(id);
					if (search == null) {
						return null;
					}
					return {
						id,
						name: search.name,
						type: search.type,
						totalUseCount: search.totalUseCount,
					};
				})
				.filter((item): item is TabItem => item != null),
		[tabIds, searchMap],
	);

	// Build available items (not in current tabs)
	const availableItems: Array<TabItem> = useMemo(() => {
		const tabIdSet = new Set(tabIds);
		return searches
			.filter((search) => !tabIdSet.has(getSearchId(search)))
			.sort((a, b) => b.totalUseCount - a.totalUseCount)
			.map((search) => ({
				id: getSearchId(search),
				name: search.name,
				type: search.type,
				totalUseCount: search.totalUseCount,
			}));
	}, [searches, tabIds]);

	// Combined items for unified SortableContext (enables cross-column dragging)
	const allItems = useMemo(
		() => [...tabItems, ...availableItems],
		[tabItems, availableItems],
	);

	// Get active item for drag overlay
	const activeItem = useMemo(() => {
		if (activeId == null) {
			return null;
		}
		return (
			tabItems.find((item) => item.id === activeId) ??
			availableItems.find((item) => item.id === activeId) ??
			null
		);
	}, [activeId, tabItems, availableItems]);

	// DnD sensors
	const sensors = useSensors(
		useSensor(PointerSensor, {
			activationConstraint: {
				distance: 5,
			},
		}),
	);

	// Custom collision detection for multi-container drag-and-drop
	// Ensures both containers are properly detected for visual feedback and event handling
	const customCollisionDetection: CollisionDetection = useCallback(
		(args) => {
			const pointerCollisions = pointerWithin(args);

			if (pointerCollisions.length === 0) {
				return closestCenter(args);
			}

			// Find container collisions
			const containerCollision = pointerCollisions.find((collision) =>
				CONTAINER_IDS.has(String(collision.id)),
			);

			if (containerCollision != null) {
				const containerId = String(containerCollision.id);

				const containerItemIds =
					containerId === TABS_DROP_ZONE
						? new Set(tabIds)
						: new Set(availableItems.map((item) => item.id));

				const containerDroppables = args.droppableContainers.filter(
					(container) => containerItemIds.has(String(container.id)),
				);

				if (containerDroppables.length > 0) {
					const result = closestCenter({
						...args,
						droppableContainers: containerDroppables,
					});
					if (result.length > 0) {
						return result;
					}
				}

				return [containerCollision];
			}

			// If no container collision found but we have item collisions,
			// determine which container the items belong to and include that container
			const availableItemIds = new Set(availableItems.map((item) => item.id));
			const tabItemIds = new Set(tabIds);

			// Check if over an available item
			const overAvailableItem = pointerCollisions.find((collision) =>
				availableItemIds.has(String(collision.id)),
			);

			if (overAvailableItem != null) {
				// Find the AVAILABLE_DROP_ZONE container and include it
				const availableContainer = args.droppableContainers.find(
					(container) => String(container.id) === AVAILABLE_DROP_ZONE,
				);
				if (availableContainer != null) {
					// Return both the container (for isOver feedback) and the item
					return [
						{ id: AVAILABLE_DROP_ZONE, data: availableContainer.data },
						overAvailableItem,
					];
				}
			}

			// Check if over a tab item
			const overTabItem = pointerCollisions.find((collision) =>
				tabItemIds.has(String(collision.id)),
			);

			if (overTabItem != null) {
				const tabsContainer = args.droppableContainers.find(
					(container) => String(container.id) === TABS_DROP_ZONE,
				);
				if (tabsContainer != null) {
					return [
						{ id: TABS_DROP_ZONE, data: tabsContainer.data },
						overTabItem,
					];
				}
			}

			// Fallback: Use rectIntersection to check containers directly
			// This handles the case where Available zone is empty
			const rectCollisions = rectIntersection(args);
			const containerFromRect = rectCollisions.find((collision) =>
				CONTAINER_IDS.has(String(collision.id)),
			);
			if (containerFromRect != null) {
				return [containerFromRect];
			}

			return pointerCollisions;
		},
		[tabIds, availableItems],
	);

	const handleDragStart = useCallback(
		(event: DragStartEvent) => {
			setActiveId(String(event.active.id));
			previousTabIds.current = [...tabIds]; // Save for cancellation
		},
		[tabIds],
	);

	// Move items between containers during drag (canonical dnd-kit pattern)
	const handleDragOver = useCallback(
		(event: DragOverEvent) => {
			const { active, over } = event;
			if (over == null) {
				return;
			}

			const activeIdStr = String(active.id);
			const overIdStr = String(over.id);

			const isActiveInTabs = tabIds.includes(activeIdStr);
			const isOverInTabs =
				tabIds.includes(overIdStr) || overIdStr === TABS_DROP_ZONE;
			const isOverInAvailable =
				availableItems.some((item) => item.id === overIdStr) ||
				overIdStr === AVAILABLE_DROP_ZONE;

			// Moving from tabs to available
			if (isActiveInTabs && isOverInAvailable) {
				setTabIds((prev) => prev.filter((id) => id !== activeIdStr));
			}
			// Moving from available to tabs
			else if (!isActiveInTabs && isOverInTabs) {
				const overIndex = tabIds.indexOf(overIdStr);
				const insertAt = overIndex >= 0 ? overIndex : tabIds.length;
				setTabIds((prev) => {
					if (prev.includes(activeIdStr)) {
						return prev;
					} // Already moved
					const newIds = [...prev];
					newIds.splice(insertAt, 0, activeIdStr);
					return newIds;
				});
			}
		},
		[tabIds, availableItems],
	);

	const handleDragEnd = useCallback(
		(event: DragEndEvent) => {
			const { active, over } = event;
			setActiveId(null);

			if (over == null) {
				setTabIds(previousTabIds.current);
				return;
			}

			const activeIdStr = String(active.id);
			const overIdStr = String(over.id);

			// Handle reordering within tabs (cross-container movement already handled by handleDragOver)
			const isActiveInTabs = tabIds.includes(activeIdStr);
			const isOverInTabs = tabIds.includes(overIdStr);

			if (isActiveInTabs && isOverInTabs) {
				const activeIndex = tabIds.indexOf(activeIdStr);
				const overIndex = tabIds.indexOf(overIdStr);
				if (activeIndex !== overIndex) {
					setTabIds(arrayMove(tabIds, activeIndex, overIndex));
				}
			}
		},
		[tabIds],
	);

	const onDragCancel = useCallback(() => {
		setActiveId(null);
		setTabIds(previousTabIds.current);
	}, []);

	const handleSave = useCallback(() => {
		onSave(tabIds);
	}, [onSave, tabIds]);

	const hasChanges = useMemo(() => {
		if (tabIds.length !== currentTabIds.length) {
			return true;
		}
		return tabIds.some((id, index) => id !== currentTabIds[index]);
	}, [tabIds, currentTabIds]);

	return (
		<div
			className={clsx(
				'w-[820px] max-w-[calc(100vw-2rem)] rounded-lg shadow-lg overflow-hidden',
				colors.classes.bgPrimary,
				colors.classes.borderDefault,
				'border',
			)}
		>
			<DndContext
				sensors={sensors}
				collisionDetection={customCollisionDetection}
				onDragStart={handleDragStart}
				onDragOver={handleDragOver}
				onDragEnd={handleDragEnd}
				onDragCancel={onDragCancel}
			>
				{/* Single SortableContext enables cross-column dragging */}
				<SortableContext
					items={allItems.map((item) => item.id)}
					strategy={verticalListSortingStrategy}
				>
					<div className="flex divide-x divide-muted">
						{/* My Tabs column */}
						<div className="flex-1 p-4">
							<h3
								className={clsx(
									'text-sm font-semibold mb-3',
									colors.classes.textPrimary,
								)}
							>
								{tabsLabel ?? t('My Tabs')}
							</h3>
							<DroppableZone id={TABS_DROP_ZONE}>
								{tabItems.map((item) => (
									<DraggableTabChip key={item.id} item={item} />
								))}
								{tabItems.length === 0 && (
									<div
										className={clsx(
											'text-center py-8 text-sm',
											colors.classes.textMuted,
										)}
									>
										{t('Drag searches here to add as tabs')}
									</div>
								)}
							</DroppableZone>
						</div>

						{/* Available Lists column */}
						<div className="flex-1 p-4">
							<h3
								className={clsx(
									'text-sm font-semibold mb-3',
									colors.classes.textPrimary,
								)}
							>
								{t('Available Searches')}
							</h3>
							<DroppableZone id={AVAILABLE_DROP_ZONE}>
								{availableItems.map((item) => (
									<DraggableTabChip key={item.id} item={item} />
								))}
								{availableItems.length === 0 && (
									<div
										className={clsx(
											'text-center py-8 text-sm',
											colors.classes.textMuted,
										)}
									>
										{t('All searches are already in tabs')}
									</div>
								)}
							</DroppableZone>
						</div>
					</div>
				</SortableContext>

				{/* Portal DragOverlay to document.body to escape any CSS
				   stacking context from the dropdown's absolute positioning */}
				{createPortal(
					<DragOverlay>
						{activeItem != null ? <TabChipOverlay item={activeItem} /> : null}
					</DragOverlay>,
					document.body,
				)}
			</DndContext>

			{/* Footer with actions */}
			<div
				className={clsx(
					'flex items-center justify-between px-4 py-3 border-t',
					colors.classes.borderDefault,
				)}
			>
				<div className="flex items-center gap-2">
					{/* Custom tab management buttons */}
					{isCustom && isEnabled && onDisable != null && (
						<Button
							variant="outline"
							size="sm"
							onClick={onDisable}
							disabled={isSaving}
						>
							{t('Disable My Tabs')}
						</Button>
					)}
					{isCustom && !isEnabled && onEnable != null && (
						<Button
							variant="outline"
							size="sm"
							onClick={onEnable}
							disabled={isSaving}
						>
							{t('Re-enable My Tabs')}
						</Button>
					)}
					{isCustom && onReset != null && (
						<Button
							variant="danger"
							size="sm"
							onClick={onReset}
							disabled={isSaving}
						>
							{t('Reset to Defaults')}
						</Button>
					)}
				</div>
				<div className="flex items-center gap-3">
					<Button variant="outline" onClick={onCancel} disabled={isSaving}>
						{t('Cancel')}
					</Button>
					<Button
						variant="primary"
						onClick={handleSave}
						disabled={isSaving || !hasChanges}
						loading={isSaving}
					>
						{t('Save')}
					</Button>
				</div>
			</div>
		</div>
	);
}
